"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const staging_1 = require("./extension/staging");
const merge_1 = require("./helper/merge");
const isObject = require('isobject');
class TwingExtensionSet {
    constructor() {
        this.initialized = false;
        this.runtimeInitialized = false;
        this.functionCallbacks = [];
        this.filterCallbacks = [];
        this.staging = new staging_1.TwingExtensionStaging();
        this.extensions = new Map();
        this.parsers = [];
    }
    /**
     * Initializes the runtime environment.
     */
    initRuntime(env) {
        if (this.runtimeInitialized) {
            return;
        }
        this.runtimeInitialized = true;
        for (let [name, extension] of this.extensions) {
            let candidate = extension;
            if (candidate.TwingExtensionInitRuntimeInterfaceImpl) {
                candidate.TwingExtensionInitRuntimeInterfaceImpl.initRuntime(env);
            }
        }
    }
    hasExtension(name) {
        return this.extensions.has(name);
    }
    getExtension(name) {
        return this.extensions.get(name);
    }
    /**
     * Registers an array of extensions.
     *
     * @param {Array<TwingExtensionInterface>} extensions An array of extensions
     */
    setExtensions(extensions) {
        for (let extension of extensions) {
            this.addExtension(extension);
        }
    }
    /**
     * Returns all registered extensions.
     *
     * @return Map<string, TwingExtensionInterface> A hash of extensions
     */
    getExtensions() {
        return this.extensions;
    }
    getSignature() {
        return JSON.stringify([...this.extensions.keys()]);
    }
    isInitialized() {
        return this.initialized || this.runtimeInitialized;
    }
    getNodeVisitors() {
        if (!this.initialized) {
            this.initExtensions();
        }
        return this.visitors;
    }
    getTokenParsers() {
        if (!this.initialized) {
            this.initExtensions();
        }
        return this.parsers;
    }
    getGlobals() {
        if (this.globals !== undefined) {
            return this.globals;
        }
        let globals = new Map();
        for (let [name, extension] of this.getExtensions()) {
            let candidate = extension;
            if (!candidate.TwingExtensionGlobalsInterfaceImpl) {
                continue;
            }
            let extGlobals = candidate.TwingExtensionGlobalsInterfaceImpl.getGlobals();
            if (!(extGlobals instanceof Map)) {
                throw new Error(`"${extension.constructor.name}[TwingExtensionGlobalsInterface].getGlobals()" must return a Map of globals.`);
            }
            globals = merge_1.merge(globals, extGlobals);
        }
        if (this.initialized) {
            this.globals = globals;
        }
        return globals;
    }
    /**
     * Registers an extension.
     *
     * @param {TwingExtensionInterface} extension A TwingExtensionInterface instance
     * @param {String} name An optional name the extension will be registered as
     */
    addExtension(extension, name = null) {
        if (!name) {
            name = extension.constructor.name;
        }
        if (this.initialized) {
            throw new Error(`Unable to register extension "${name}" as extensions have already been initialized.`);
        }
        if (this.extensions.has(name)) {
            throw new Error(`Unable to register extension "${name}" as it is already registered.`);
        }
        this.extensions.set(name, extension);
    }
    addTokenParser(parser) {
        if (this.initialized) {
            throw new Error('Unable to add a token parser as extensions have already been initialized.');
        }
        this.staging.addTokenParser(parser);
    }
    /**
     * Gets the registered unary Operators.
     *
     * @return Map<string, TwingOperator> A map of unary operator definitions
     */
    getUnaryOperators() {
        if (!this.initialized) {
            this.initExtensions();
        }
        return this.unaryOperators;
    }
    /**
     * Gets the registered binary Operators.
     *
     * @return Map<string, TwingOperator> A map of binary operators
     */
    getBinaryOperators() {
        if (!this.initialized) {
            this.initExtensions();
        }
        return this.binaryOperators;
    }
    addFunction(twingFunction) {
        if (this.initialized) {
            throw new Error(`Unable to add function "${twingFunction.getName()}" as extensions have already been initialized.`);
        }
        this.staging.addFunction(twingFunction);
    }
    getFunctions() {
        if (!this.initialized) {
            this.initExtensions();
        }
        return this.functions;
    }
    /**
     * Get a function by name.
     *
     * @param {string} name         function name
     * @returns {TwingFunction}     A TwingFunction instance or null if the function does not exist
     */
    getFunction(name) {
        if (!this.initialized) {
            this.initExtensions();
        }
        if (this.functions.has(name)) {
            return this.functions.get(name);
        }
        for (let [pattern, twingFunction] of this.functions) {
            let count = 0;
            pattern = pattern.replace(/\*/g, function (match, value) {
                count++;
                return '(.*?)';
            });
            if (count) {
                let regExp = new RegExp('^' + pattern + '$', 'g');
                let match = regExp.exec(name);
                let matches = [];
                if (match) {
                    for (let i = 1; i <= count; i++) {
                        matches.push(match[i]);
                    }
                    twingFunction.setArguments(matches);
                    return twingFunction;
                }
            }
        }
        for (let callback of this.functionCallbacks) {
            let function_ = callback(name);
            if (function_ !== false) {
                return function_;
            }
        }
        return null;
    }
    registerUndefinedFunctionCallback(callable) {
        this.functionCallbacks.push(callable);
    }
    addFilter(filter) {
        if (this.initialized) {
            throw new Error(`Unable to add filter "${filter.getName()}" as extensions have already been initialized.`);
        }
        this.staging.addFilter(filter);
    }
    getFilters() {
        if (!this.initialized) {
            this.initExtensions();
        }
        return this.filters;
    }
    /**
     * Get a filter by name.
     *
     * Subclasses may override this method and load filters differently;
     * so no list of filters is available.
     *
     * @param string name The filter name
     *
     * @return Twig_Filter|false A Twig_Filter instance or false if the filter does not exist
     */
    getFilter(name) {
        if (!this.initialized) {
            this.initExtensions();
        }
        if (this.filters.has(name)) {
            return this.filters.get(name);
        }
        let filter;
        let pattern;
        for ([pattern, filter] of this.filters) {
            let count = 0;
            pattern = pattern.replace(/\*/g, function (match, value) {
                count++;
                return '(.*?)';
            });
            if (count) {
                let regExp = new RegExp('^' + pattern + '$', 'g');
                let match = regExp.exec(name);
                let matches = [];
                if (match) {
                    for (let i = 1; i <= count; i++) {
                        matches.push(match[i]);
                    }
                    filter.setArguments(matches);
                    return filter;
                }
            }
        }
        for (let callback of this.filterCallbacks) {
            let filter = callback.call(name);
            if (filter !== false) {
                return filter;
            }
        }
        return null;
    }
    registerUndefinedFilterCallback(callable) {
        this.filterCallbacks.push(callable);
    }
    addNodeVisitor(visitor) {
        if (this.initialized) {
            throw new Error('Unable to add a node visitor as extensions have already been initialized.');
        }
        this.staging.addNodeVisitor(visitor);
    }
    addTest(test) {
        if (this.initialized) {
            throw new Error(`Unable to add test "${test.getName()}" as extensions have already been initialized.`);
        }
        this.staging.addTest(test);
    }
    /**
     *
     * @returns {Map<string, TwingTest>}
     */
    getTests() {
        if (!this.initialized) {
            this.initExtensions();
        }
        return this.tests;
    }
    /**
     * Gets a test by name.
     *
     * @param {string} name The test name
     * @returns {TwingTest} A TwingTest instance or null if the test does not exist
     */
    getTest(name) {
        if (!this.initialized) {
            this.initExtensions();
        }
        if (this.tests.has(name)) {
            return this.tests.get(name);
        }
        let test;
        let pattern;
        for ([pattern, test] of this.tests) {
            let count = 0;
            pattern = pattern.replace(/\*/g, function (match, value) {
                count++;
                return '(.*?)';
            });
            if (count) {
                let regExp = new RegExp('^' + pattern + '$', 'g');
                let match = regExp.exec(name);
                let matches = [];
                if (match) {
                    for (let i = 1; i <= count; i++) {
                        matches.push(match[i]);
                    }
                    test.setArguments(matches);
                    return test;
                }
            }
        }
        return null;
    }
    initExtensions() {
        this.parsers = [];
        this.filters = new Map();
        this.functions = new Map();
        this.tests = new Map();
        this.visitors = [];
        this.unaryOperators = new Map();
        this.binaryOperators = new Map();
        for (let [key, extension] of this.extensions) {
            this.initExtension(extension);
        }
        this.initExtension(this.staging);
        // Done at the end only, so that an exception during initialization does not mark the environment as initialized when catching the exception
        this.initialized = true;
    }
    initExtension(extension) {
        // filters
        // BC, @see https://github.com/ericmorand/twing/issues/218
        if (Array.isArray(extension.getFilters())) {
            for (let [key, filter] of extension.getFilters().entries()) {
                this.filters.set(filter.getName(), filter);
            }
        }
        else {
            for (let [key, filter] of extension.getFilters()) {
                this.filters.set(filter.getName(), filter);
            }
        }
        // functions
        // BC, @see https://github.com/ericmorand/twing/issues/218
        if (Array.isArray(extension.getFunctions())) {
            for (let [key, function_] of extension.getFunctions().entries()) {
                this.functions.set(function_.getName(), function_);
            }
        }
        else {
            for (let [key, function_] of extension.getFunctions()) {
                this.functions.set(function_.getName(), function_);
            }
        }
        // tests
        for (let test of extension.getTests()) {
            this.tests.set(test.getName(), test);
        }
        // token parsers
        for (let parser of extension.getTokenParsers()) {
            // if (!parser instanceof Twig_TokenParserInterface) {
            //     throw new Error('getTokenParsers() must return an array of Twig_TokenParserInterface.');
            // }
            this.parsers.push(parser);
        }
        // node visitors
        for (let visitor of extension.getNodeVisitors()) {
            this.visitors.push(visitor);
        }
        // operators
        let operators = extension.getOperators();
        if (operators) {
            if (!Array.isArray(operators)) {
                throw new Error(`"${extension.constructor.name}.getOperators()" must return an array with operators, got "${isObject(operators) ? operators.constructor.name : typeof operators}".`);
            }
            if (operators.length !== 2) {
                throw new Error(`"${extension.constructor.name}.getOperators()" must return an array of 2 elements, got ${operators.length}.`);
            }
            this.unaryOperators = new Map([...this.unaryOperators, ...operators[0]]);
            this.binaryOperators = new Map([...this.binaryOperators, ...operators[1]]);
        }
    }
}
exports.TwingExtensionSet = TwingExtensionSet;
